/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component } from '@angular/core';
import { Validators } from '@angular/forms';
import { LucideEye, LucideEyeOff } from '@lucide/angular';
import { TriggerUserLoginV3, UserLoginFailed } from '../../store/user-auth.actions';
import { UserAuthState } from '../../store/user-auth.state';
import { LoginFormComponent } from '../login-form/login-form.component';

@Component({
  selector: 'ptl-v3-login-form',
  templateUrl: './v3-login-form.component.html',
  styleUrls: ['./v3-login-form.component.scss'],
})
export class V3LoginFormComponent extends LoginFormComponent {
  protected readonly LucideEye = LucideEye;
  protected readonly LucideEyeOff = LucideEyeOff;

  get organizationInitial(): string {
    return this.organization?.name?.trim()?.charAt(0)?.toUpperCase() || '';
  }

  // v3 already shows a separate SSO button, so form fields don't need v2's reveal gate.
  override get showFormLoginFields(): boolean {
    return this.isFormAuthType || this.showMagicLinkMode;
  }

  /**
   * Replaces the base class's pathname-based (`/external/signin`) TOTP special-casing with an
   * error-code-driven flow: always attempt email/password first.
   *
   * Keycloak's direct-grant token endpoint cannot be trusted to distinguish "wrong password" from
   * "correct password, but this account has TOTP enrolled and none was sent" — confirmed in
   * production, both return an identical `{"error":"invalid_grant","error_description":"Invalid
   * user credentials"}`. But we don't need Keycloak for this: `force_two_factor_authentication` is
   * an org-level setting already loaded pre-login (same call that renders the org's name/logo/
   * background on this page), so we know up front whether 2FA can *possibly* be the reason for a
   * failure. Only orgs with that flag on get the "enter your code" reveal on an ambiguous failure;
   * every other org's failures are treated as plain wrong-credentials, exactly as before, with no
   * irrelevant TOTP prompt. The reveal is also not counted as a failed attempt yet — a genuinely
   * first-time, unenrolled user still succeeds outright on their first try and never sees the field
   * at all, while an enrolled user just needs to add their code and resubmit. Only a failure on the
   * *second* attempt (once a code could have been supplied) counts toward the lockout warning.
   *
   * KNOWN GAP: if an org disables `shouldEnforceTwoFactorAuth` after some of its users already
   * enrolled in TOTP, those users' Keycloak credentials are still live and Keycloak still demands a
   * code from them — but this gate treats their ambiguous failure as a flat wrong-password rejection
   * (no reveal), locking them out. See YUNA — reverted a fix that removed the gate entirely because
   * it made every wrong-password attempt at *every* org briefly reveal the code field; needs a
   * better-targeted fix (e.g. only revert the gate for orgs known to have legacy TOTP enrollees).
   */
  protected override submitForm(): void {
    if (this.showMagicLinkMode) {
      return;
    }

    const emailControl = this.authenticationDetails.get('email');
    const passwordControl = this.authenticationDetails.get('password');
    if (!emailControl?.valid || !passwordControl?.valid) {
      return;
    }

    const email = emailControl.value?.trim();
    const password = passwordControl.value?.trim();

    if (!this.showTotpInput) {
      this.snackBar.dismiss();
      this.checkAndSetJiscDeepLink();
      this.loginButtonPress$.next(true);
      const orgRequiresTotp = this.store.selectSnapshot(UserAuthState.shouldEnforceTwoFactorAuth);
      this.store.dispatch(new TriggerUserLoginV3(email, password)).subscribe(() => {
        const error = this.store.selectSnapshot(UserAuthState.lastLoginErrorV3);
        if (!error) {
          return;
        }
        if (error.code === 'OTHER' || !orgRequiresTotp) {
          // Either unrelated to credentials/2FA (network error, org misconfiguration, etc.), or
          // this org doesn't use 2FA at all — TOTP can't be the reason, so this is a real failure.
          this.store.dispatch(new UserLoginFailed(error.message));
          return;
        }
        this.showTotpInput = true;
        this.authenticationDetails.get('totp')?.setValidators([Validators.required]);
        this.authenticationDetails.get('totp')?.updateValueAndValidity();
      });
      return;
    }

    if (this.authenticationDetails.valid) {
      const totpValue = this.authenticationDetails.get('totp')?.value?.trim();
      this.snackBar.dismiss();
      this.checkAndSetJiscDeepLink();
      this.loginButtonPress$.next(true);
      this.store.dispatch(new TriggerUserLoginV3(email, password, totpValue || undefined)).subscribe(() => {
        const error = this.store.selectSnapshot(UserAuthState.lastLoginErrorV3);
        if (error) {
          this.store.dispatch(new UserLoginFailed(error.message));
        }
      });
    }
  }
}
